import { type DesignBody, type DesignNode, isPointInShape } from '@repo/api-contracts';
import { hitTestLayers, type PhotoDimensions, type PointMm } from '@repo/design-renderer';
import type { Raster } from '@repo/warp-engine';
import {
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  type BoxNode,
  clientToPhotoPx,
  componentMmToPhoto,
  dragMove,
  dragResize,
  dragResizeEdge,
  dragRotate,
  dragTextCorner,
  dragTextStretch,
  dragTextWidth,
  type FlatView,
  findNode,
  HANDLE_HIT_TOLERANCE_PX,
  type HandleKind,
  handleCanvasKeyDown,
  hitWarpedHandle,
  type InlineEditing,
  isEdgeKind,
  localPxPerMm,
  nextInlineTextSession,
  photoToComponentMm,
  unionRectOf,
  useCanvasViewport,
  warpedHandles,
} from '@/features/design-editor';
import { drawRasterToCanvas } from '@/shared/lib';
import { cn } from '@/shared/ui';
import type { EditSurface, FlatEditCanvasProps } from './flat-edit-canvas';
import { InlineTextEditor } from './inline-text-editor';
import { RotationFeedback } from './rotation-feedback';
import { TextEditHint } from './text-edit-hint';
import { WarpedAreaOutline } from './warped-area-outline';
import { WarpedSelectionOverlay } from './warped-selection-overlay';

/** guide 는 사진 px 안내선이다(평면 캔버스와 달리 영역 mm 로 편 것이 아니다). */
type WarpedEditCanvasProps = Omit<FlatEditCanvasProps, 'warped'> & {
  /** 손을 뗀 상태의 곡면 합성(원래 해상도). 낡았거나 아직 없으면 null. */
  readonly warped: ImageBitmap | null;
  /** 본문만 바꿔 낮은 해상도로 다시 그린 합성. 그릴 수 없으면 null(마지막 그림을 유지한다). */
  readonly renderLive: (body: DesignBody) => Raster | null;
  /** 목업 사진 크기(px) — 포인터를 사진 px 로 바꾸는 기준. */
  readonly photo: PhotoDimensions;
  /** 지금 보는 면·색상(예: `front:<colorOptionId>`). 마지막 그림은 같은 면·색상일 때만 남긴다. */
  readonly sceneKey: string;
};

interface Drag {
  readonly kind: HandleKind;
  readonly original: BoxNode;
  readonly componentId: string;
  /** 누른 자리의 영역 mm. 회전 핸들처럼 표면 밖이면 null(이동에만 쓴다). */
  readonly startMm: PointMm | null;
}

interface SelectedBox {
  readonly node: BoxNode;
  readonly componentId: string;
}

interface Picked {
  readonly nodeId: string | null;
  readonly componentId: string | null;
}

const documentOf = (surfaces: readonly EditSurface[]) => ({
  productId: '',
  colorOptionId: '',
  components: surfaces.map((surface) => ({
    componentId: surface.component.id,
    layers: [...surface.layers],
  })),
});

const selectedBoxOf = (surfaces: readonly EditSurface[], id: string | null): SelectedBox | null => {
  if (id === null) return null;
  const found = findNode(documentOf(surfaces), id);
  return found !== null && found.node.type !== 'group'
    ? { node: found.node, componentId: found.componentId }
    : null;
};

/** 이 면 본문 — 끄는 중이면 그 노드만 live 값으로 바꾸고, 바로 고치는 글자(hiddenId)는 빼고 그린다(입력칸이 대신 보인다). */
const bodyOf = (
  surfaces: readonly EditSurface[],
  live: BoxNode | null,
  hiddenId: string | null = null,
): DesignBody => ({
  components: surfaces.map((surface) => ({
    componentId: surface.component.id,
    layers: surface.layers
      .filter((layer) => layer.id !== hiddenId)
      .map((layer): DesignNode => (live?.id === layer.id ? live : layer)),
  })),
});

/**
 * 곡면(원통·격자) 상품의 휜 모습 그대로 편집하는 캔버스(U2). 사진 상자 위 한 캔버스에 손을 뗀 상태는 곡면 합성(warped),
 * 끄는 동안은 프레임마다 낮은 해상도로 다시 그린 합성(renderLive)을 보인다. 포인터는 사진 px → 영역 mm 로 되돌려
 * 평면 편집과 같은 끌기 규칙(이동·크기·회전·글상자 폭)을 쓴다. 표면 밖으로 나가 mm 를 못 얻으면 마지막 값을 유지한다.
 * 키보드·더블클릭·Esc·Delete·보기 모드(휠 확대·빈 곳 끌기)는 평면 편집 캔버스와 같다.
 */
export const WarpedEditCanvas = ({
  surfaces,
  space,
  photo,
  activeComponentId,
  onActivate,
  selectedNodeId,
  guide,
  onSelect,
  onChangeNode,
  onNudge,
  onDeleteSelected,
  backdrop = null,
  onTextInput,
  editRequest = null,
  onBackdropError,
  materials,
  warped,
  renderLive,
  sceneKey,
  onWarpedHit,
  fitMode = 'photo',
  onFitModeChange,
  drawImpl = drawRasterToCanvas,
}: WarpedEditCanvasProps): ReactElement => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const guideRef = useRef<HTMLCanvasElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [live, setLive] = useState<BoxNode | null>(null);
  const [editing, setEditing] = useState<InlineEditing | null>(null);
  /** 열린 입력칸의 '지금 값 올리기'. */
  const flushInline = useRef<(() => void) | null>(null);
  /** 손을 뗀 상태에서 마지막으로 낮은 해상도로 그린 본문(영역별 레이어 배열)·렌더 함수·면·색상. */
  const idleDrawn = useRef<{
    readonly layers: readonly (readonly DesignNode[])[];
    readonly renderLive: (body: DesignBody) => Raster | null;
    readonly sceneKey: string;
    readonly hiddenId: string | null;
  } | null>(null);
  /** 캔버스에 지금 남아 있는 그림의 면·색상. 비어 있으면 null. */
  const drawnScene = useRef<string | null>(null);

  const viewport = useCanvasViewport<Picked>({
    space,
    focus: unionRectOf(surfaces.map((surface) => surface.rect)),
    fitMode,
    onFitModeChange,
  });
  const { hostRef, photoBox, hostRect, capture, release } = viewport;
  /** 화면 px ÷ 사진 px. */
  const screenPerPhotoPx = photoBox.width / photo.widthPx;
  const surfaceOf = (componentId: string): EditSurface | undefined =>
    surfaces.find((surface) => surface.component.id === componentId);

  const stored = selectedBoxOf(surfaces, selectedNodeId);
  const selected: SelectedBox | null =
    live !== null && drag !== null ? { node: live, componentId: drag.componentId } : stored;
  const selectedSlot = selected === null ? undefined : surfaceOf(selected.componentId);
  const dragging = drag !== null;
  // 바로 고치는 중인 텍스트 — 고른 노드가 그 텍스트일 때만(선택이 바뀌거나 노드가 사라지면 닫힌다).
  const editingText =
    editing !== null &&
    onTextInput !== undefined &&
    selected !== null &&
    selected.node.id === editing.nodeId &&
    selected.node.type === 'text'
      ? selected.node
      : null;
  const hiddenId = editingText?.id ?? null;

  useEffect(() => {
    if (editing !== null && hiddenId === null) setEditing(null);
  }, [editing, hiddenId]);

  const openTextEditor = (nodeId: string) => {
    if (onTextInput === undefined) return;
    setEditing({ nodeId, session: nextInlineTextSession(nodeId) });
  };

  // 새로 넣은 텍스트 — 바로 고치기를 열어 기본 문구를 고른 채 두므로 입력하면 바로 바뀐다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: 요청 순번이 바뀔 때만 연다.
  useEffect(() => {
    if (editRequest !== null) openTextEditor(editRequest.nodeId);
  }, [editRequest?.seq]);

  /**
   * 낮은 해상도 합성을 그리고 걸린 시간을 남긴다. 그릴 수 없으면 false. 시간은 상태가 아니라 호스트의
   * data-render-ms 에 바로 적는다 — 끄는 프레임마다 다시 렌더하지 않는다(측정·e2e 가 이 속성을 읽는다).
   */
  const drawLive = (body: DesignBody): boolean => {
    const canvas = canvasRef.current;
    if (canvas === null) return false;
    const started = performance.now();
    const raster = renderLive(body);
    if (raster === null) return false;
    try {
      drawImpl(canvas, raster);
    } catch {
      return false;
    }
    drawnScene.current = sceneKey;
    hostRef.current?.setAttribute(
      'data-render-ms',
      String(Math.round((performance.now() - started) * 10) / 10),
    );
    return true;
  };

  // 끄는 동안 — 프레임마다 live 를 반영한 본문으로 다시 그린다(같은 프레임 안 이동은 마지막 것만 그린다).
  // biome-ignore lint/correctness/useExhaustiveDependencies: live 가 바뀔 때만 다음 프레임을 예약한다.
  useEffect(() => {
    if (!dragging || live === null) return;
    idleDrawn.current = null;
    const frame = requestAnimationFrame(() => {
      drawLive(bodyOf(surfaces, live));
    });
    return () => cancelAnimationFrame(frame);
  }, [live, dragging]);

  // 손을 뗀 상태 — 곡면 합성이 있으면 그것을, 아직 없으면(방금 놓았거나 패널·키보드로 고친 직후) 지금 본문을 낮은 해상도로 그린다.
  // 글자를 바로 고치는 동안은 곡면 합성 대신 그 글자를 뺀 본문을 그린다(펴서 겹친 입력칸과 글자가 겹치지 않게).
  // biome-ignore lint/correctness/useExhaustiveDependencies: 합성·본문·끌기 상태가 바뀔 때만 다시 그린다.
  useEffect(() => {
    if (dragging) return;
    const canvas = canvasRef.current;
    if (canvas === null) return;
    if (warped !== null && hiddenId === null) {
      canvas.width = warped.width;
      canvas.height = warped.height;
      canvas.getContext('2d')?.drawImage(warped, 0, 0);
      drawnScene.current = sceneKey;
      idleDrawn.current = null;
      return;
    }
    // 부모가 다시 렌더해 surfaces 배열만 새로 왔을 때는 같은 본문을 다시 그리지 않는다.
    const layers = surfaces.map((surface) => surface.layers);
    const previous = idleDrawn.current;
    if (
      previous !== null &&
      previous.renderLive === renderLive &&
      previous.sceneKey === sceneKey &&
      previous.hiddenId === hiddenId &&
      previous.layers.length === layers.length &&
      previous.layers.every((entry, index) => entry === layers[index])
    )
      return;
    const frame = requestAnimationFrame(() => {
      if (drawLive(bodyOf(surfaces, null, hiddenId))) {
        idleDrawn.current = { layers, renderLive, sceneKey, hiddenId };
        return;
      }
      // 그릴 재료가 아직 없을 때 — 같은 면·색상이면(들어온 직후·본문만 바뀜) 마지막 그림을 그대로 둔다: 지우면 곡면
      // 합성이 올 때까지 저장된 디자인이 사라져 보인다. 다른 면·색상의 그림이면 새 사진 위에 남기지 않고 지워 사진만 보인다.
      if (drawnScene.current !== null && drawnScene.current !== sceneKey) {
        canvas.width = 0;
        canvas.height = 0;
        drawnScene.current = null;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [warped, surfaces, dragging, renderLive, sceneKey, hiddenId]);

  // 안내선 — 곡면은 사진 px 안내선을 그대로 받아 노드를 고른 동안 사진 상자에 늘여 겹친다(평면 투영을 거치지 않는다).
  const guideShown = guide !== null && selected !== null;
  useEffect(() => {
    if (guideRef.current === null || guide === null || !guideShown) return;
    try {
      drawImpl(guideRef.current, guide);
    } catch {
      // 안내선 래스터가 아직 없으면 다음 렌더에서 그린다.
    }
  }, [guide, guideShown, drawImpl]);

  const photoPxOf = (event: { clientX: number; clientY: number }) => {
    const rect = hostRect();
    return clientToPhotoPx(
      { x: event.clientX, y: event.clientY },
      {
        left: rect.left + photoBox.left,
        top: rect.top + photoBox.top,
        width: photoBox.width,
        height: photoBox.height,
      },
      photo,
    );
  };

  const mmIn = (componentId: string, event: { clientX: number; clientY: number }) => {
    const slot = surfaceOf(componentId);
    return slot === undefined ? null : photoToComponentMm(slot, photo, photoPxOf(event));
  };

  /** 누른 자리를 고른다 — 역워프 히트(hitTestPreview)로 노드를, 없으면 누른 자리가 든 영역(빈 곳)을 알린다. */
  const pickAt = (event: { clientX: number; clientY: number }): Picked => {
    const point = photoPxOf(event);
    const ordered = [...surfaces].reverse();
    const localHit = (): string | null => {
      for (const surface of ordered) {
        const mm = photoToComponentMm(surface, photo, point);
        if (mm === null || !isPointInShape(surface.component, mm)) continue;
        const hit = hitTestLayers(surface.layers, mm);
        if (hit !== null) return hit.groupPath[0] ?? hit.nodeId;
      }
      return null;
    };
    const nodeId = onWarpedHit === undefined ? localHit() : onWarpedHit(point);
    if (nodeId !== null)
      return { nodeId, componentId: findNode(documentOf(surfaces), nodeId)?.componentId ?? null };
    const under = ordered.find((surface) => {
      const mm = photoToComponentMm(surface, photo, point);
      return mm !== null && isPointInShape(surface.component, mm);
    });
    return { nodeId: null, componentId: under?.component.id ?? null };
  };

  const beginDrag = (
    kind: HandleKind,
    original: BoxNode,
    componentId: string,
    event: { clientX: number; clientY: number },
  ) => {
    setDrag({ kind, original, componentId, startMm: mmIn(componentId, event) });
  };

  const settleEmptyClick = (picked: Picked) => {
    if (picked.componentId !== null && picked.componentId !== activeComponentId)
      onActivate(picked.componentId);
    onSelect(null);
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    // 글자를 바로 고치는 중에 바깥을 누르면 — 조합 중인 글자까지 먼저 올리고 고치기를 마친 뒤 누름을 처리한다.
    if (editing !== null) {
      flushInline.current?.();
      setEditing(null);
    }
    capture(event.pointerId);
    if (selected !== null && selectedSlot !== undefined) {
      const kind = hitWarpedHandle(
        warpedHandles(selectedSlot, photo, selected.node, screenPerPhotoPx),
        photoPxOf(event),
        HANDLE_HIT_TOLERANCE_PX / screenPerPhotoPx,
      );
      if (kind !== null) {
        beginDrag(kind, selected.node, selected.componentId, event);
        return;
      }
    }
    const picked = pickAt(event);
    if (picked.nodeId === null) {
      if (fitMode === 'area') {
        viewport.startPan(event, picked);
        return;
      }
      settleEmptyClick(picked);
      return;
    }
    if (picked.componentId !== null && picked.componentId !== activeComponentId)
      onActivate(picked.componentId);
    onSelect(picked.nodeId);
    const box = selectedBoxOf(surfaces, picked.nodeId);
    if (box !== null) beginDrag('move', box.node, box.componentId, event);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (viewport.movePan(event)) return;
    if (drag === null) return;
    // 표면(격자·실루엣·감김 조각) 밖이면 mm 가 없다 — 마지막 live 값을 그대로 둔다.
    const current = mmIn(drag.componentId, event);
    if (current === null) return;
    const { kind, original, startMm } = drag;
    if (kind === 'move') {
      if (startMm !== null) setLive(dragMove(original, startMm, current));
    } else if (kind === 'rotate') setLive(dragRotate(original, current, event.shiftKey));
    else if (kind === 'side-l' || kind === 'side-r') {
      if (original.type === 'text') setLive(dragTextWidth(original, kind, current));
    } else if (isEdgeKind(kind)) {
      if (original.type === 'image') setLive(dragResizeEdge(original, kind, current));
      else if (kind === 'edge-t' || kind === 'edge-b')
        setLive(dragTextStretch(original, kind, current));
    } else if (original.type === 'text')
      setLive(dragTextCorner(original, kind, current, event.shiftKey)); // Shift = 폭·세로 늘림 따로
    else setLive(dragResize(original, kind, current, event.shiftKey)); // Shift = 비율 유지
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    release(event.pointerId);
    const panned = viewport.finishPan();
    if (panned !== null) {
      if (!panned.moved) settleEmptyClick(panned.payload);
      return;
    }
    if (live !== null) onChangeNode(live);
    setLive(null);
    setDrag(null);
  };

  const onPointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    release(event.pointerId);
    viewport.cancelPan();
    setLive(null);
    setDrag(null);
  };

  const onDoubleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (onTextInput === undefined) return;
    const picked = pickAt(event);
    if (picked.nodeId === null) return;
    const target = findNode(documentOf(surfaces), picked.nodeId);
    if (target?.node.type === 'text') openTextEditor(target.node.id);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && selected?.node.type === 'text' && onTextInput !== undefined) {
      event.preventDefault();
      openTextEditor(selected.node.id);
      return;
    }
    handleCanvasKeyDown(event, {
      selectedNodeId,
      hasBox: selected !== null,
      onSelect,
      onDeleteSelected,
      onNudge,
    });
  };

  /** 글자 고치기 안내 자리 — 노드 중심의 국소 축척으로 편 보기(안내 한 줄의 자리만 정한다). */
  const hintView = (slot: EditSurface, node: BoxNode): FlatView | null => {
    const center = { xMm: node.x + node.width / 2, yMm: node.y + node.height / 2 };
    const at = componentMmToPhoto(slot, photo, center);
    if (at === null) return null;
    const scale = localPxPerMm(slot, photo, center);
    const pxPerMmX = scale.x * screenPerPhotoPx;
    const pxPerMmY = scale.y * screenPerPhotoPx;
    return {
      pxPerMmX,
      pxPerMmY,
      offsetX: photoBox.left + at.x * screenPerPhotoPx - center.xMm * pxPerMmX,
      offsetY: photoBox.top + at.y * screenPerPhotoPx - center.yMm * pxPerMmY,
    };
  };
  const textHint =
    selected !== null &&
    selectedSlot !== undefined &&
    selected.node.type === 'text' &&
    !dragging &&
    editingText === null &&
    onTextInput !== undefined
      ? hintView(selectedSlot, selected.node)
      : null;
  // 바로 고치는 입력칸 자리 — 휜 모습 대신 노드 가운데의 국소 축척으로 편 근사 자리에 평평하게 겹친다.
  const editorView =
    editingText !== null && selectedSlot !== undefined ? hintView(selectedSlot, editingText) : null;

  // 회전 핸들을 끄는 동안만 — 노드 가운데의 국소 축척으로 편 화면 좌표에 각도 표시·직각 안내선을 둔다.
  const rotationView =
    selected !== null && selectedSlot !== undefined && drag?.kind === 'rotate'
      ? hintView(selectedSlot, selected.node)
      : null;

  return (
    <div
      ref={hostRef}
      role="application"
      aria-label="디자인 편집 캔버스"
      data-testid="warped-edit-canvas"
      data-mode="warped"
      data-active-component={activeComponentId}
      data-selected-x={selected?.node.x ?? ''}
      data-render-ms=""
      // biome-ignore lint/a11y/noNoninteractiveTabindex: 키보드로 미세 이동·삭제하는 편집 표면이라 role=application 으로 포커스를 받는다.
      tabIndex={0}
      data-fit-mode={fitMode}
      className={cn(
        'relative size-full touch-none overflow-hidden select-none outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        fitMode === 'area' && (viewport.panMoved ? 'cursor-grabbing' : 'cursor-grab'),
      )}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onKeyDown={onKeyDown}
    >
      {backdrop !== null && (
        <img
          src={backdrop.url}
          alt=""
          aria-hidden
          draggable={false}
          data-testid="warped-backdrop"
          className="pointer-events-none absolute max-w-none select-none"
          style={photoBox}
          onError={onBackdropError}
        />
      )}
      <canvas
        ref={canvasRef}
        aria-hidden
        data-testid="warped-edit-surface"
        className="pointer-events-none absolute"
        style={photoBox}
      />
      {guideShown && (
        <canvas
          ref={guideRef}
          aria-hidden
          data-testid="warped-guide"
          className="pointer-events-none absolute opacity-70"
          style={photoBox}
        />
      )}
      {surfaces.map((surface) => (
        <WarpedAreaOutline
          key={surface.component.id}
          slot={surface}
          photo={photo}
          box={photoBox}
          active={surface.component.id === activeComponentId}
          emphasized={selected !== null && selected.componentId === surface.component.id}
        />
      ))}
      {selected !== null && selectedSlot !== undefined && (
        <WarpedSelectionOverlay
          slot={selectedSlot}
          node={selected.node}
          photo={photo}
          box={photoBox}
          onPointerDownHandle={(kind, event) => {
            event.stopPropagation();
            capture(event.pointerId);
            beginDrag(kind, selected.node, selected.componentId, event);
          }}
          rotating={drag?.kind === 'rotate'}
        />
      )}
      {selected !== null && textHint !== null && (
        <TextEditHint node={selected.node} view={textHint} />
      )}
      {rotationView !== null && selected !== null && (
        <RotationFeedback node={selected.node} view={rotationView} viewport={viewport.size} />
      )}
      {editing !== null && editingText !== null && editorView !== null && (
        <InlineTextEditor
          key={editing.session}
          flushRef={flushInline}
          node={editingText}
          view={editorView}
          materials={materials}
          onInput={(text) => onTextInput?.(editing.nodeId, text, editing.session)}
          onFinish={(reason) => {
            setEditing(null);
            if (reason === 'escape') hostRef.current?.focus({ preventScroll: true });
          }}
        />
      )}
    </div>
  );
};
